/**
 * Spec-driven mapping and validation engine for batch imports.
 *
 * The whole file is processed in one pass and every problem is collected —
 * never fail-fast — so users can fix everything and re-upload once. Any
 * error rejects the upload as a whole; the store is only touched by the
 * spec's commit step when validation produces zero errors.
 */

import { isCellError } from './extract';
import { countWords } from './wordCount';
import { normalizeName, resolveByName } from './resolve';

/**
 * @typedef {object} ImportError
 * @property {number|null} row Excel row number as shown in the spreadsheet; null for file-level problems
 * @property {string|null} column Template header the problem relates to; null for file-level problems
 * @property {string} message User-facing description of the problem
 */

const escapeHtml = (value) => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

/**
 * Converts a plain-text cell into the HTML shape TinyMCE-backed fields store.
 * Spreadsheet content is always treated as literal text, never as markup.
 *
 * @param {string} value Plain text, possibly multi-line
 * @returns {string|null} Paragraph-wrapped HTML, or null when empty
 */
export const textToHtml = (value) => {
  const paragraphs = value
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => `<p>${escapeHtml(line)}</p>`);

  return paragraphs.length ? paragraphs.join('') : null;
};

/**
 * Parses a numeric cell that may arrive as text with currency formatting
 * (e.g. "R 1,200.50"). Returns null when the value is not a clean number.
 */
const parseNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const cleaned = String(value).replace(/[R,\s ]/gi, '');

  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;

  return parseFloat(cleaned);
};

/**
 * Maps each spec column to its index in the uploaded header row.
 * Headers are matched loosely (trim, case, whitespace) but the set must
 * correspond exactly to the template — missing or unknown columns are errors.
 */
const matchHeaders = (spec, headers) => {
  const errors = [];
  const normalizedHeaders = headers.map(header => header === null ? null : normalizeName(header));
  const indexByKey = {};

  spec.columns.forEach(column => {
    const matches = normalizedHeaders
      .map((header, index) => ({ header, index }))
      .filter(entry => entry.header === normalizeName(column.header));

    // Header problems live in the spreadsheet's first row
    if (matches.length === 0) {
      errors.push({ row: 1, column: column.header, message: `The file is missing the "${column.header}" column. Please download and use the provided template.` });
    } else if (matches.length > 1) {
      errors.push({ row: 1, column: column.header, message: `The "${column.header}" column appears more than once.` });
    } else {
      indexByKey[column.key] = matches[0].index;
    }
  });

  const specHeaders = spec.columns.map(column => normalizeName(column.header));

  normalizedHeaders.forEach((header, index) => {
    if (header !== null && !specHeaders.includes(header)) {
      errors.push({ row: 1, column: headers[index], message: `Unrecognised column "${headers[index]}". Please download and use the provided template.` });
    }
  });

  return { indexByKey, errors };
};

const coerceValue = (column, raw, addError) => {
  if (column.type === 'number') {
    const parsed = parseNumber(raw);

    if (parsed === null) {
      addError(`"${raw}" is not a valid number.`);
      return null;
    }

    return parsed;
  }

  const text = String(raw).trim();

  if (column.type === 'text') {
    return textToHtml(text);
  }

  return text === '' ? null : text;
};

/**
 * Runs the full mapping + validation pipeline for one import definition.
 *
 * @param {object} spec Import definition (see specs/)
 * @param {{ headers: string[], rows: Array<{ excelRow: number, cells: any[] }> }} extracted Neutral table from the extraction layer
 * @param {object} planStore Active plan store, used for link candidates and section word limits
 * @returns {{ result: object|null, errors: ImportError[] }} result is null whenever there are errors
 */
export const mapAndValidate = (spec, extracted, planStore) => {
  const errors = [];

  const { indexByKey, errors: headerErrors } = matchHeaders(spec, extracted.headers);
  errors.push(...headerErrors);

  if (errors.length > 0) return { result: null, errors };

  if (extracted.rows.length === 0) {
    return { result: null, errors: [{ row: null, column: null, message: 'The file contains no data rows.' }] };
  }

  const context = spec.buildContext(planStore);
  const mappedRows = [];

  extracted.rows.forEach(({ excelRow, cells }) => {
    const values = {};
    const links = {};

    spec.columns.forEach(column => {
      const raw = cells[indexByKey[column.key]];
      const addError = (message) => errors.push({ row: excelRow, column: column.header, message });

      values[column.key] = null;

      if (isCellError(raw)) {
        addError(`Cell contains a spreadsheet error value (${raw.__cellError}).`);
        return;
      }

      if (raw === null) {
        if (column.required) addError(`"${column.header}" is required.`);
        return;
      }

      const value = coerceValue(column, raw, addError);

      if (value === null) {
        if (column.required) addError(`"${column.header}" is required.`);
        return;
      }

      if (column.wordLimit && countWords(value) > column.wordLimit) {
        addError(`"${column.header}" exceeds the ${column.wordLimit}-word limit (${countWords(value)} words).`);
        return;
      }

      if (column.validateValue) {
        const message = column.validateValue(value);

        if (message) {
          addError(message);
          return;
        }
      }

      values[column.key] = value;

      if (column.link) {
        const { noun, candidates, autoCreate } = context[column.link];
        const resolution = resolveByName(value, candidates);

        if (resolution.status === 'matched') {
          links[column.key] = resolution.candidate;
        } else if (resolution.status === 'ambiguous') {
          const names = resolution.candidates.map(candidate => `"${candidate.name}"`).join(', ');
          addError(`"${value}" is ambiguous — it matches more than one ${noun} (${names}).`);
        } else if (autoCreate) {
          links[column.key] = null;
        } else {
          const hint = resolution.bestGuess ? ` Did you mean "${resolution.bestGuess.name}"?` : '';
          addError(`No ${noun} matches "${value}".${hint}`);
        }
      }
    });

    mappedRows.push({ excelRow, values, links });
  });

  if (spec.validateRows) {
    spec.validateRows(mappedRows, (row, column, message) => errors.push({ row, column, message }));
  }

  if (errors.length > 0) return { result: null, errors: sortErrors(spec, errors) };

  let id = Date.now();
  const nextId = () => id++;

  const result = spec.buildResult(mappedRows, context, nextId);

  if (spec.validateSectionLimit) {
    const message = spec.validateSectionLimit(result, planStore);

    if (message) errors.push({ row: null, column: null, message });
  }

  if (errors.length > 0) return { result: null, errors: sortErrors(spec, errors) };

  return { result, errors: [] };
};

const sortErrors = (spec, errors) => {
  const columnOrder = spec.columns.map(column => column.header);

  return [...errors].sort((a, b) => {
    if ((a.row ?? -1) !== (b.row ?? -1)) return (a.row ?? -1) - (b.row ?? -1);

    return columnOrder.indexOf(a.column) - columnOrder.indexOf(b.column);
  });
};
